import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Loader2,
  Server,
  Building2,
  BookOpen,
  Package,
  FileText,
  ClipboardList,
  Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { externalSystemService, profileService, type ServiceContext, type UsageCounts } from '@/services';
import RecordMetadata from '@/components/RecordMetadata';
import ExternalSystemFormDialog, { ExternalSystemRecord } from '@/components/externalsystem/ExternalSystemFormDialog';

interface ConsolidatedCardProps {
  title: string;
  icon: React.ReactNode;
  parentLabel: string;
  parentCount: number;
  itemLabel: string;
  itemCount?: number;
  itemActiveCount?: number;
  itemInactiveCount?: number;
  showActiveInactive?: boolean;
  onParentClick?: () => void;
}

function ConsolidatedUsageCard({
  title,
  icon,
  parentLabel,
  parentCount,
  itemLabel,
  itemCount,
  itemActiveCount,
  itemInactiveCount,
  showActiveInactive,
  onParentClick,
}: ConsolidatedCardProps) {
  const isClickable = parentCount > 0 && onParentClick;
  
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <div className="text-muted-foreground">{icon}</div>
      </CardHeader>
      <CardContent>
        <div className="flex gap-6">
          {/* Parent counts */}
          <div className="flex-1">
            <p className="text-xs text-muted-foreground mb-1">{parentLabel}</p>
            <p 
              className={`text-2xl font-bold ${isClickable ? 'text-primary cursor-pointer hover:underline' : ''}`}
              onClick={isClickable ? onParentClick : undefined}
            >
              {parentCount}
            </p>
          </div>
          {/* Item counts */}
          <div className="flex-1 border-l pl-6">
            <p className="text-xs text-muted-foreground mb-1">{itemLabel}</p>
            {showActiveInactive ? (
              <div className="flex flex-col gap-1">
                <Badge variant="default" className="w-fit">{itemActiveCount ?? 0} Active</Badge>
                <Badge variant="outline" className="w-fit">{itemInactiveCount ?? 0} Inactive</Badge>
              </div>
            ) : (
              <p className="text-2xl font-bold">{itemCount ?? 0}</p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

interface SimpleCardProps {
  title: string;
  icon: React.ReactNode;
  count: number;
  onClick?: () => void;
}

function SimpleUsageCard({ title, icon, count, onClick }: SimpleCardProps) {
  const isClickable = count > 0 && onClick;
  
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <div className="text-muted-foreground">{icon}</div>
      </CardHeader>
      <CardContent>
        <p 
          className={`text-2xl font-bold ${isClickable ? 'text-primary cursor-pointer hover:underline' : ''}`}
          onClick={isClickable ? onClick : undefined}
        >
          {count}
        </p>
      </CardContent>
    </Card>
  );
}

export default function ExternalSystemDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSnapshot, setSelectedSnapshot] = useState<Record<string, unknown> | null>(null);

  // Fetch user profile
  const { data: profile } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const result = await profileService.get(user.id);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!user?.id,
  });

  const companyId = profile?.company_id;

  // Fetch external system details with audit names
  const { data: externalSystem, isLoading } = useQuery({
    queryKey: ['external-system', id],
    queryFn: async () => {
      const result = await externalSystemService.getWithAuditNames(id!);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!id,
  });

  // Fetch usage counts
  const { data: usageCounts, isLoading: usageLoading } = useQuery({
    queryKey: ['external-system-usage', externalSystem?.name, companyId],
    queryFn: async () => {
      if (!externalSystem?.name || !companyId) return null;
      const result = await externalSystemService.getUsageCounts(externalSystem.name, companyId);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!externalSystem?.name && !!companyId,
  });

  const getServiceContext = (): ServiceContext => ({
    companyId: companyId!,
    userId: user?.id,
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data: Omit<ExternalSystemRecord, 'id'>) => {
      if (!id || !selectedSnapshot) throw new Error('Missing required data');
      const result = await externalSystemService.update(id, data, selectedSnapshot, getServiceContext());
      if (result.error) throw result.error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['external-system', id] });
      queryClient.invalidateQueries({ queryKey: ['external-systems'] });
      toast.success('External system updated');
    },
    onError: (error) => {
      toast.error('Failed to update external system');
      console.error(error);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!externalSystem) throw new Error('No external system');
      const snapshot = {
        name: externalSystem.name,
        description: externalSystem.description,
      };
      const result = await externalSystemService.delete(id!, snapshot, getServiceContext());
      if (result.error) throw result.error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['external-systems'] });
      toast.success('External system deleted');
      navigate('/configuration?tab=external-systems');
    },
    onError: (error) => {
      toast.error('Failed to delete external system');
      console.error(error);
    },
  });

  const handleEditClick = () => {
    if (externalSystem) {
      setSelectedSnapshot({
        name: externalSystem.name,
        description: externalSystem.description,
      });
      setEditDialogOpen(true);
    }
  };

  const handleSave = async (data: Omit<ExternalSystemRecord, 'id'>) => {
    await updateMutation.mutateAsync(data);
    setEditDialogOpen(false);
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    await deleteMutation.mutateAsync();
    setDeleteDialogOpen(false);
  };

  const getTotalAffectedRecords = (counts: UsageCounts): number => {
    return externalSystemService.getTotalAffectedRecords(counts);
  };

  const getAffectedTablesList = (counts: UsageCounts): string[] => {
    const affected: string[] = [];
    if (counts.vendors.total > 0) affected.push(`${counts.vendors.total} Vendor(s)`);
    if (counts.price_books.total > 0) affected.push(`${counts.price_books.total} Price Book(s)`);
    const pbItems = counts.price_book_items.active + counts.price_book_items.inactive;
    if (pbItems > 0) affected.push(`${pbItems} Price Book Item(s)`);
    if (counts.invoices.total > 0) affected.push(`${counts.invoices.total} Invoice(s)`);
    if (counts.invoice_items.total > 0) affected.push(`${counts.invoice_items.total} Invoice Item(s)`);
    if (counts.tickets.total > 0) affected.push(`${counts.tickets.total} Ticket(s)`);
    if (counts.ticket_items.total > 0) affected.push(`${counts.ticket_items.total} Ticket Item(s)`);
    return affected;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!externalSystem) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground mb-4">External system not found.</p>
        <Button variant="outline" onClick={() => navigate('/configuration?tab=external-systems')}>
          Back to External Systems
        </Button>
      </div>
    );
  }

  const isInUse = usageCounts ? getTotalAffectedRecords(usageCounts) > 0 : false;
  const affectedTables = usageCounts ? getAffectedTablesList(usageCounts) : [];

  return (
    <div className="animate-slide-up">
      {/* Back button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate('/configuration?tab=external-systems')}
        className="mb-4 -ml-2"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to External Systems
      </Button>

      {/* Header Card */}
      <div className="bg-card border border-border rounded-lg p-6 mb-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Server className="w-7 h-7 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-bold text-foreground mb-2">{externalSystem.name}</h1>
              {externalSystem.description && (
                <p className="text-muted-foreground">{externalSystem.description}</p>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleEditClick}>
              Edit
            </Button>
            <Button variant="destructive" onClick={handleDeleteClick}>
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          </div>
        </div>

        <div className="mt-4">
          <RecordMetadata record={externalSystem} />
        </div>
      </div>

      {/* Usage Dashboard */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold mb-4">Usage Statistics</h2>
        {usageLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : usageCounts ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <ConsolidatedUsageCard
              title="Price Books"
              icon={<BookOpen className="w-4 h-4" />}
              parentLabel="Price Books"
              parentCount={usageCounts.price_books.total}
              itemLabel="Price Book Items"
              itemActiveCount={usageCounts.price_book_items.active}
              itemInactiveCount={usageCounts.price_book_items.inactive}
              showActiveInactive
              onParentClick={() => navigate(`/price-books?external_system=${encodeURIComponent(externalSystem.name)}`)}
            />
            <ConsolidatedUsageCard
              title="Invoices"
              icon={<FileText className="w-4 h-4" />}
              parentLabel="Invoices"
              parentCount={usageCounts.invoices.total}
              itemLabel="Invoice Items"
              itemCount={usageCounts.invoice_items.total}
              onParentClick={() => navigate(`/invoices?external_system=${encodeURIComponent(externalSystem.name)}`)}
            />
            <ConsolidatedUsageCard
              title="Tickets"
              icon={<ClipboardList className="w-4 h-4" />}
              parentLabel="Tickets"
              parentCount={usageCounts.tickets.total}
              itemLabel="Ticket Items"
              itemCount={usageCounts.ticket_items.total}
            />
            <SimpleUsageCard
              title="Vendors"
              icon={<Building2 className="w-4 h-4" />}
              count={usageCounts.vendors.total}
              onClick={() => navigate(`/vendors?external_system=${encodeURIComponent(externalSystem.name)}`)}
            />
          </div>
        ) : (
          <p className="text-muted-foreground">Unable to load usage statistics.</p>
        )}
      </div>

      {/* Edit Dialog */}
      <ExternalSystemFormDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        externalSystem={externalSystem as ExternalSystemRecord}
        onSave={handleSave}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isInUse ? 'External System In Use' : 'Delete External System'}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              {isInUse ? (
                <>
                  <p>
                    This external system is currently referenced by records in the following tables:
                  </p>
                  <ul className="list-disc list-inside text-sm">
                    {affectedTables.map((table, idx) => (
                      <li key={idx}>{table}</li>
                    ))}
                  </ul>
                  <p className="font-medium text-destructive">
                    Deleting this external system will set the external_system field to null on{' '}
                    {getTotalAffectedRecords(usageCounts!)} record(s).
                  </p>
                </>
              ) : (
                <p>
                  Are you sure you want to delete "{externalSystem.name}"? This action cannot be
                  undone.
                </p>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isInUse ? 'Delete Anyway' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
